/**
 * Copyright (c) 2010-2022 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { UntypedFormBuilder, FormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { LoggerService } from '../../../../../../modules/shared/services/logger.service';
import { Actions, ofActionSuccessful, Store } from '@ngxs/store';
import { PasswordComplexity, PasswordRequirements } from '../../../../state/auth.model';
import { UpdatePasswordComplexityUI } from '../../../../state/auth.actions';
import { PasswordComplexityValidator } from '../../password-complexity.validator';
import { ResetPasswordService } from '../../../../services/reset-password.service';
import { AuthState } from '../../../../state/auth.state';
import { filter, map, switchMap, switchMapTo, take } from 'rxjs/operators';
import { BasePasswordFormComponent } from '../base-password-form.component';
import { LayoutDirective, LayoutAlignDirective } from '@ngbracket/ngx-layout/flex';
import { MatFormField, MatError } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { PasswordComplexityComponent } from '../../complexity/password-complexity.component';
import { MatButton } from '@angular/material/button';

@Component({
    selector: 'dash-gis-reset-password-form',
    templateUrl: './reset-password-form.component.html',
    styleUrls: ['./reset-password-form.component.scss'],
    imports: [
        FormsModule,
        ReactiveFormsModule,
        LayoutDirective,
        LayoutAlignDirective,
        MatFormField,
        MatInput,
        MatError,
        PasswordComplexityComponent,
        MatButton,
    ],
})
export class ResetPasswordFormComponent extends BasePasswordFormComponent implements OnChanges, OnInit {
    requirements: PasswordRequirements;
    complexity: PasswordComplexity = { score: 0, feedback: { warning: '', suggestions: [''] } };

    @Input() allowClose = true;
    @Input() submitText = 'Reset Password';

    constructor(
        private logger: LoggerService,
        private fb: UntypedFormBuilder,
        private actions$: Actions,
        private store: Store,
        private resetService: ResetPasswordService
    ) {
        super();
        this.passwordForm = this.fb.group({
            newPassword: ['', Validators.required],
            newPasswordConfirm: ['', Validators.required],
        });
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.requireCurrent && changes.requireCurrent.firstChange) {
            this.passwordForm.addControl('current', this.fb.control('', Validators.required));
        }
    }

    ngOnInit(): void {
        this.store
            .select(AuthState.loaded)
            .pipe(
                filter((l) => l),
                take(1),
                switchMap(() => this.store.select(AuthState.methodConfig)),
                map((fn) => fn('local')),
                take(1)
            )
            .subscribe((config) => {
                this.requirements = config?.passwordRequirements || { minStrength: 0, minLength: 6 };
                const complexityValidator = PasswordComplexityValidator.createValidator(
                    this.resetService,
                    this.store,
                    this.requirements
                );

                this.newPassword.setValidators([
                    Validators.required,
                    Validators.minLength(this.requirements.minLength),
                ]);
                this.newPassword.setAsyncValidators(complexityValidator);
                this.newPasswordConfirm.setValidators(Validators.required);

                this.passwordForm.setValidators([this.matchValidator]);

                this.passwordForm.updateValueAndValidity();
            });

        this.actions$.pipe(ofActionSuccessful(UpdatePasswordComplexityUI)).subscribe((action) => {
            this.complexity = action.complexity;
        });
    }
}
